import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './CouponAnalytics.css'; // We will add styles to this file

const API_URL = process.env.REACT_APP_API_URL;

// --- FIX: Use the same CSS classes as the main Reports page for consistency ---
const MetricCard = ({ title, value }) => (
    <div className="analytics-page-metric-card">
        <p className="analytics-page-metric-card__title">{title}</p>
        <p className="analytics-page-metric-card__value">{value}</p>
    </div>
);

const CouponAnalytics = () => {
    const [coupons, setCoupons] = useState([]);
    const [selectedCouponCode, setSelectedCouponCode] = useState('');
    const [allUsageDetails, setAllUsageDetails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const fetchInitialData = async () => {
            if (!API_URL) {
                setError("API URL is not configured.");
                setLoading(false);
                return;
            }
            try {
                setLoading(true);
                const token = localStorage.getItem('adminToken');
                const config = { headers: { Authorization: `Bearer ${token}` } };
                const [couponsRes, usageRes] = await Promise.all([
                    axios.get(`${API_URL}/api/coupons`, config),
                    axios.get(`${API_URL}/api/coupons/usage/all`, config)
                ]);
                setCoupons(couponsRes.data || []);
                setAllUsageDetails(usageRes.data.usageDetails || []);
            } catch (err) {
                setError('Failed to fetch analytics data. Please try again.');
                console.error(err);
            } finally {
                setLoading(false);
            }
        };
        fetchInitialData();
    }, []);

    const analyticsData = useMemo(() => {
        if (allUsageDetails.length === 0) {
            return { totalUsage: 0, totalDiscount: '0.00', mostUsed: 'N/A' };
        }
        const totalDiscount = allUsageDetails.reduce((sum, record) => sum + (record.discountAmount || 0), 0);
        const usageCounts = allUsageDetails.reduce((acc, record) => {
            acc[record.couponCode] = (acc[record.couponCode] || 0) + 1;
            return acc;
        }, {});
        const mostUsed = Object.entries(usageCounts).sort((a, b) => b[1] - a[1])[0];
        return {
            totalUsage: allUsageDetails.length,
            totalDiscount: totalDiscount.toFixed(2),
            mostUsed: mostUsed ? `${mostUsed[0]} (${mostUsed[1]} times)` : 'N/A',
        };
    }, [allUsageDetails]);

    const displayedUsageDetails = useMemo(() => {
        if (!selectedCouponCode) return allUsageDetails;
        return allUsageDetails.filter(record => record.couponCode === selectedCouponCode);
    }, [selectedCouponCode, allUsageDetails]);

    if (loading) return <div className="loading-center">Loading analytics...</div>;
    if (error) return <p className="error-message">{error}</p>;

    return (
        <div className="coupon-analytics-container">
            {/* --- FIX: Use a grid for the metric cards, just like the main Reports page --- */}
            <div className="analytics-page-grid is-cols-4">
                <MetricCard title="Total Coupons Created" value={coupons.length} />
                <MetricCard title="Total Times a Coupon Was Used" value={analyticsData.totalUsage} />
                <MetricCard title="Total Discount Given" value={`₹${analyticsData.totalDiscount}`} />
                <MetricCard title="Most Used Coupon" value={analyticsData.mostUsed} />
            </div>

            {/* --- FIX: A dedicated container for the filter controls --- */}
            <div className="filter-controls-wrapper">
                <label htmlFor="coupon-select">Filter by Coupon:</label>
                <select id="coupon-select" value={selectedCouponCode} onChange={e => setSelectedCouponCode(e.target.value)}>
                    <option value="">-- Show All Coupons --</option>
                    {coupons.map(coupon => (
                        <option key={coupon._id} value={coupon.code}>
                            {coupon.code} - ({coupon.description})
                        </option>
                    ))}
                </select>
            </div>
            
            {/* The table now uses the shared data table styles */}
            <div className="analytics-page-data-table-wrapper">
                <div className="analytics-page-data-table-container">
                    <table className="analytics-page-data-table">
                        <thead>
                            <tr>
                                <th>User Name</th>
                                <th>User Email</th>
                                <th>Applied On (Service)</th>
                                <th>Original Amount</th>
                                <th>Discount Amount</th>
                                <th>Date & Time Used</th>
                            </tr>
                        </thead>
                        <tbody>
                            {displayedUsageDetails.length > 0 ? displayedUsageDetails.map(record => (
                                <tr key={record._id}>
                                    <td>{record.user.name}</td>
                                    <td>{record.user.email}</td>
                                    <td>{record.service.name}</td>
                                    <td>₹{record.originalAmount.toFixed(2)}</td>
                                    <td className="discount-cell">- ₹{record.discountAmount.toFixed(2)}</td>
                                    <td>{new Date(record.usedAt).toLocaleString()}</td>
                                </tr>
                            )) : (
                                <tr>
                                    <td colSpan="6" style={{ textAlign: 'center' }}>No usage data found for the selected filter.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default CouponAnalytics;